import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Leaf, Loader2, ImageIcon, Film, Type, Sparkles } from "lucide-react";
import { api, apiError, BACKEND_URL } from "../lib/api";

const GenButton = ({ label, icon: Icon, onClick, busy, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={busy}
    className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-xl px-3 py-2 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
  >
    {busy ? <Loader2 className="animate-spin" size={13} /> : <Icon size={13} />} {label}
  </button>
);

export default function AdminRenalifeMaterials() {
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(null); // `${material_id}:${kind}`
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/renalife/materials");
      setMaterials(data);
      if (data.some((m) => m.video_status === "processing")) {
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 8000);
      }
    } catch (err) { console.error("Error cargando materiales:", err); }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(timer.current);
  }, [load]);

  const generate = async (materialId, kind) => {
    setBusy(`${materialId}:${kind}`);
    try {
      const { data } = await api.post(`/admin/renalife/materials/${materialId}/generate`, { kind });
      setMaterials(data);
      toast.success(kind === "video" ? "Video en proceso (1-3 min)…" : "Generado correctamente");
      if (kind === "video" || kind === "all") { clearTimeout(timer.current); timer.current = setTimeout(load, 8000); }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-12">
      <div className="flex items-center gap-2 mb-2">
        <Leaf className="text-emerald-400" size={22} />
        <h2 className="font-display font-bold text-xl">Materiales RENALIFE (prediseñados)</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Se generan UNA sola vez y todos los distribuidores RENALIFE los ven listos en su zona. Costo aproximado: imagen ~$0.04, video ~$0.50, texto ~$0.01 USD.
      </p>
      <div className="space-y-4">
        {materials.map((m) => (
          <div key={m.material_id} data-testid={`admin-material-${m.material_id}`} className="rounded-2xl border border-emerald-500/30 bg-[#04231b] p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-bold">{m.title}</h3>
                <p className="text-xs text-zinc-400 mt-0.5">{m.business_rule}</p>
                <div className="flex items-center gap-2 mt-2 text-[11px]">
                  <span className={`px-2 py-0.5 rounded-full border ${m.image_url ? "border-green-500/40 text-green-400" : "border-zinc-600 text-zinc-500"}`}>Imagen {m.image_url ? "lista" : "pendiente"}</span>
                  <span className={`px-2 py-0.5 rounded-full border ${m.video_url ? "border-green-500/40 text-green-400" : m.video_status === "processing" ? "border-yellow-400/40 text-yellow-300" : m.video_status === "error" ? "border-red-500/40 text-red-400" : "border-zinc-600 text-zinc-500"}`}>
                    Video {m.video_url ? "listo" : m.video_status === "processing" ? "generando…" : m.video_status === "error" ? "error" : "pendiente"}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full border ${m.caption ? "border-green-500/40 text-green-400" : "border-zinc-600 text-zinc-500"}`}>Texto {m.caption ? "listo" : "pendiente"}</span>
                </div>
                {m.video_status === "error" && <p className="text-[11px] text-red-400 mt-1">Error del video: {m.video_error}</p>}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <GenButton label="Imagen" icon={ImageIcon} busy={busy === `${m.material_id}:image`} onClick={() => generate(m.material_id, "image")} testid={`gen-image-${m.material_id}`} />
                <GenButton label="Video" icon={Film} busy={busy === `${m.material_id}:video`} onClick={() => generate(m.material_id, "video")} testid={`gen-video-${m.material_id}`} />
                <GenButton label="Texto" icon={Type} busy={busy === `${m.material_id}:caption`} onClick={() => generate(m.material_id, "caption")} testid={`gen-caption-${m.material_id}`} />
                <button
                  data-testid={`gen-all-${m.material_id}`}
                  onClick={() => generate(m.material_id, "all")}
                  disabled={busy === `${m.material_id}:all`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold rounded-xl px-3 py-2 bg-[#FACC15] hover:bg-yellow-300 text-[#022C22] disabled:opacity-50"
                >
                  {busy === `${m.material_id}:all` ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Generar todo
                </button>
              </div>
            </div>
            {m.image_url && (
              <img src={`${BACKEND_URL}${m.image_url}`} alt={m.title} className="mt-4 rounded-xl border border-white/10 max-h-56 object-cover" />
            )}
            {m.video_url && (
              <video src={m.video_url} controls className="mt-3 rounded-xl border border-white/10 max-h-56 bg-black" />
            )}
            {m.caption && <p className="mt-3 text-xs text-zinc-300 whitespace-pre-wrap bg-black/30 border border-white/10 rounded-xl p-3">{m.caption}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
